import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../config/api';

const EditArtwork = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [artwork, setArtwork] = useState({
    title: '',
    price: '',
    category: '',
    verse: '',
    description: '',
    imageUrl: '', // Principal image
    images: [], // Additional images (up to 4 more)
    dimensions: '',
    material: '',
    inStock: true,
    featured: false
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [imageCollections, setImageCollections] = useState([]);
  const [selectedImageCollection, setSelectedImageCollection] = useState('all');
  const [showImageSelector, setShowImageSelector] = useState(false);
  const [currentImageType, setCurrentImageType] = useState('principal'); // New state to track which image is being selected
  const [availableImages, setAvailableImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });

  // Fetch artwork data
  useEffect(() => {
    const fetchArtwork = async () => {
      try {
        setLoading(true);
        const response = await apiService.get(`/admin/artworks/${id}`);
        
        // Make sure the images array exists
        if (!response.images) {
          response.images = [];
        }
        
        setArtwork(response);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching artwork:', error);
        setError('Failed to load artwork data. Please try again.');
        setLoading(false);
      }
    };

    fetchArtwork();
  }, [id]);

  // Fetch image collections
  useEffect(() => {
    const fetchImageCollections = async () => {
      try {
        const collections = await apiService.get('/images/collections');
        setImageCollections(['all', ...collections]);
      } catch (error) {
        console.error('Error fetching image collections:', error);
      }
    };
    
    fetchImageCollections();
  }, []);

  // Show notification
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 5000);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setArtwork({
      ...artwork,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      // Create a copy of the artwork object for price handling
      const artworkToUpdate = { 
        ...artwork,
        // Ensure price is submitted as a number
        price: parseFloat(artwork.price)
      };
      
      await apiService.put(`/admin/artworks/${id}`, artworkToUpdate);
      
      setSaving(false);
      showNotification('success', 'Artwork updated successfully');
    } catch (error) {
      console.error('Error updating artwork:', error);
      setSaving(false);
      showNotification('error', 'Failed to update artwork');
    }
  };

  // Fetch available images
  const fetchAvailableImages = async (collection = 'all') => {
    try {
      setLoadingImages(true);
      let images;
      
      if (collection === 'all') {
        images = await apiService.get('/images');
      } else {
        images = await apiService.get(`/images/collection/${collection}`);
      }
      
      setAvailableImages(images);
      setLoadingImages(false);
    } catch (error) {
      console.error('Error fetching images:', error);
      setLoadingImages(false);
    }
  };

  // Open image selector modal for a specific image type
  const openImageSelector = (imageType) => {
    setCurrentImageType(imageType);
    fetchAvailableImages(selectedImageCollection);
    setShowImageSelector(true);
  };

  // Change image collection
  const handleImageCollectionChange = (e) => {
    const collection = e.target.value;
    setSelectedImageCollection(collection);
    fetchAvailableImages(collection);
  };

  // Handle image selection based on type
  const handleImageSelect = (imageUrl) => {
    if (currentImageType === 'principal') {
      setArtwork({
        ...artwork,
        imageUrl
      });
    } else if (currentImageType === 'additional') {
      // Only add if we have less than 5 images total (1 principal + 4 additional)
      if (artwork.images.length < 4) {
        setArtwork({
          ...artwork,
          images: [...artwork.images, imageUrl]
        });
      } else {
        showNotification('error', 'Maximum of 4 additional images allowed');
      }
    }
    setShowImageSelector(false);
  };

  // Remove an additional image
  const handleRemoveImage = (index) => {
    const updatedImages = [...artwork.images];
    updatedImages.splice(index, 1);
    setArtwork({
      ...artwork,
      images: updatedImages
    });
  };

  // Get the secondary image (first in the images array)
  const getSecondaryImage = () => {
    return artwork.images[0] || '';
  };

  // Get additional images (remaining images after the secondary)
  const getAdditionalImages = () => {
    return artwork.images.slice(1);
  };

  if (loading) {
    return (
      <div className="text-center py-10">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading artwork data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
        <button 
          onClick={() => navigate('/admin/artworks')}
          className="mt-4 px-4 py-2 bg-primary text-white rounded-islamic hover:bg-primary/90 transition-colors"
        >
          Back to Artworks
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-islamic shadow-md ${
          notification.type === 'success' ? 'bg-green-100 border border-green-500 text-green-700' : 
          'bg-red-100 border border-red-500 text-red-700'
        }`}>
          <div className="flex items-center">
            {notification.type === 'success' ? (
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
            ) : (
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"></path>
              </svg>
            )}
            <span>{notification.message}</span>
            <button 
              onClick={() => setNotification({ ...notification, show: false })}
              className="ml-auto text-gray-500 hover:text-gray-800"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-primary">Edit Artwork</h1>
        <button 
          onClick={() => navigate('/admin/artworks')}
          className="btn-secondary flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to Artworks
        </button>
      </div>

      <div className="bg-white p-6 rounded-islamic shadow-md mb-6">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="title">
                Artwork Title*
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={artwork.title}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="price">
                Price*
              </label>
              <input
                type="number"
                id="price"
                name="price"
                value={artwork.price}
                onChange={handleInputChange}
                required
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="category">
                Category*
              </label>
              <select
                id="category"
                name="category"
                value={artwork.category}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">Select a category</option>
                <option value="Allah Names">Allah Names</option>
                <option value="Quranic Verses">Quranic Verses</option>
                <option value="Prophetic Sayings">Prophetic Sayings</option>
                <option value="Islamic Patterns">Islamic Patterns</option>
              </select>
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="imageUrl">
                Image URL*
              </label>
              <div className="flex space-x-2">
                <input
                  type="url"
                  id="imageUrl"
                  name="imageUrl"
                  value={artwork.imageUrl}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <button
                  type="button"
                  onClick={() => openImageSelector('principal')}
                  className="btn-secondary px-3 py-2"
                >
                  Select Image
                </button>
              </div>
              {artwork.imageUrl && (
                <div className="mt-2">
                  <img 
                    src={artwork.imageUrl} 
                    alt="Preview" 
                    className="h-24 rounded-islamic object-cover"
                  />
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="dimensions">
                Dimensions
              </label>
              <input
                type="text"
                id="dimensions"
                name="dimensions"
                value={artwork.dimensions || ''}
                onChange={handleInputChange}
                placeholder="e.g., 24 x 36 inches"
                className="w-full px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="material">
                Material
              </label>
              <input
                type="text"
                id="material"
                name="material"
                value={artwork.material || ''}
                onChange={handleInputChange}
                placeholder="e.g., Canvas, Paper, etc."
                className="w-full px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-gray-700 mb-2" htmlFor="verse">
                Quranic Verse or Meaning
              </label>
              <input
                type="text"
                id="verse"
                name="verse"
                value={artwork.verse || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-gray-700 mb-2" htmlFor="description">
                Description*
              </label>
              <textarea
                id="description"
                name="description"
                value={artwork.description || ''}
                onChange={handleInputChange}
                required
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
              ></textarea>
            </div>
            
            <div className="flex space-x-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="inStock"
                  name="inStock"
                  checked={artwork.inStock}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label htmlFor="inStock" className="ml-2 text-gray-700">
                  In Stock
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="featured"
                  name="featured"
                  checked={artwork.featured}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label htmlFor="featured" className="ml-2 text-gray-700">
                  Featured
                </label>
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex space-x-4">
            <button 
              type="submit" 
              className="btn-primary flex items-center"
              disabled={saving}
            >
              {saving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
            <button 
              type="button" 
              onClick={() => navigate('/admin/artworks')}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-islamic hover:bg-gray-300 transition-colors"
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* Image Selector Modal */}
      {showImageSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-islamic p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-primary">Select an Image</h3>
              <button 
                onClick={() => setShowImageSelector(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Collection
              </label>
              <select
                value={selectedImageCollection}
                onChange={handleImageCollectionChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-islamic focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {imageCollections.map((collection, index) => (
                  <option key={index} value={collection}>
                    {collection === 'all' ? 'All Collections' : collection}
                  </option>
                ))}
              </select>
            </div>
            
            {loadingImages ? (
              <div className="text-center py-10">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading images...</p>
              </div>
            ) : (
              <>
                {availableImages.length === 0 ? (
                  <p className="text-center py-10 text-gray-500">No images found. Please upload some images first.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {availableImages.map((imageUrl, index) => (
                      <div 
                        key={index} 
                        className="border rounded-islamic overflow-hidden cursor-pointer hover:border-primary transition-colors"
                        onClick={() => handleImageSelect(imageUrl)}
                      >
                        <img 
                          src={imageUrl} 
                          alt={`Image ${index}`} 
                          className="w-full h-32 object-cover"
                        />
                        <div className="p-2 text-xs truncate">{imageUrl.split('/').pop()}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            
            <div className="mt-6 flex justify-end">
              <button 
                onClick={() => setShowImageSelector(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-islamic hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditArtwork;